import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import _ from "lodash";
import type React from "react";
import { forwardRef, ReactNode, type RefObject, useEffect, useState } from "react";
import { ChangeHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import Text, { Base } from "@/atoms-react/text/Text.react.tsx";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { useNativeInputs } from "@/features/utils/is-mobile";
import type { AutoCompleteEmailType } from "@/features/utils/types.ts";
import FormattedInputDate from "./FormattedInputDate.react";

type InputProps = {
  label?: string;
  borderless?: boolean;
  value?: string;
  placeholder?: string;
  inputComponent?: React.ReactNode;
  inputClassName?: string;
  className?: string;
  minLength?: number;
  maxLength?: number;
  isPhoneNumber?: boolean;
  noLatinCheck?: boolean;
  format?: "iban" | "date" | "phone" | "creditCard" | ((value: string) => string);
  disabled?: boolean;
  onChange?: (value: string) => void | ChangeHandler | Promise<void>;
  suffixIcon?: React.ReactNode | React.ReactNode[];
  onClickSuffixIcon?: () => void;
  rows?: number;
  error?: string;
  bgClass?: string;
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  helper?: string;
  dataCy?: string;
  autoTrim?: boolean;
} & Omit<Omit<React.ComponentPropsWithoutRef<"input">, "value">, "onChange">;

const inputFormatsMap = {
  iban: (value: string) =>
    value
      .replace(/\D/g, "")
      .replace(/(.{4})/g, "$1 ")
      .trim(),
  date: (value: string) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d{2})(\d{4})/, "$1 / $2 / $3")
      .trim(),
  creditCard: (value: string) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{4})/g, "$1 ")
      .trim()
};

export const defaultInputClassName = `bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 hover:border-slate-100 w-full font-semibold rounded-sm px-4 py-3 border-2 duration-300 border-slate-50 dark:border-slate-800 text-black dark:text-white dark:hover:bg-slate-900 dark:hover:border-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 dark:focus:border-blue-500 placeholder:font-semibold placeholder:text-slate-400 dark:placeholder:text-slate-600`;

const InputLabel = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      name,
      label,
      borderless,
      value,
      placeholder,
      inputComponent,
      className,
      minLength,
      maxLength,
      format,
      disabled,
      isPhoneNumber,
      error,
      onChange,
      suffixIcon,
      onClickSuffixIcon,
      rows,
      bgClass,
      onMouseDown,
      onClick,
      helper,
      noLatinCheck,
      dataCy,
      autoTrim = true,
      ...props
    }: InputProps,
    ref
  ) => {
    const { t } = useTranslation(["atoms"]);
    const [showPassword, setShowPassword] = useState(false);
    const [AutoCompleteSuggestions, setAutoCompleteSuggestions] = useState<AutoCompleteEmailType>({
      activeSuggestion: 0,
      suggestions: [],
      showSuggestions: false
    });
    const borderlessClass = borderless ? "border-0 focus:bg-slate-50 dark:focus:bg-slate-900" : "";
    const origin = useOrigin();
    const inputClassName = twMerge(
      defaultInputClassName,
      borderlessClass,
      origin !== FromURL.None ? "  focus:border-slate-200 dark:focus:border-slate-200" : "",
      bgClass
        ? "bg-slate-50 hover:bg-slate-50 hover:border-slate-50"
        : "hover:bg-slate-100 hover:border-slate-100",
      disabled ? "pointer-events-none pointer-events-none opacity-60 text-opacity-70" : "",
      className
    );

    function formatValue(value: string) {
      let formattedValue = value;

      if (_.isFunction(format)) {
        formattedValue = format(value);
      } else if (_.isString(format) && inputFormatsMap[format]) {
        formattedValue = inputFormatsMap[format](value);
      }
      return formattedValue;
    }
    const addAutocompleteSuggestions = (value: string) => {
      if (value.includes("@")) {
        resetSuggestion();
        return;
      }
      const emails = ["@gmail.com", "@yahoo.com", "@hotmail.com", "@outlook.com", "@icloud.com"];
      const suggestions: string[] = [];
      emails.forEach((element) => {
        suggestions.push(value + element);
      });
      if (suggestions && value)
        setAutoCompleteSuggestions({
          activeSuggestion: 0,
          suggestions: suggestions,
          showSuggestions: true
        });
      else resetSuggestion();
    };
    const selectSuggestion = (value: string) => {
      onChange?.(value ?? "");
      resetSuggestion();
    };
    const resetSuggestion = () => {
      setAutoCompleteSuggestions({
        activeSuggestion: 0,
        suggestions: [],
        showSuggestions: false
      });
    };
    const onkeydownSuggestion = (e: { keyCode: number }) => {
      const { activeSuggestion, suggestions } = AutoCompleteSuggestions;
      if (suggestions.length > 0) {
        if (e.keyCode === 13 || e.keyCode === 27) {
          resetSuggestion();
        } else if (e.keyCode === 38) {
          if (activeSuggestion === 0) {
            return;
          }
          onChange?.(suggestions[activeSuggestion - 1]);
          setAutoCompleteSuggestions({
            activeSuggestion: activeSuggestion - 1,
            suggestions: suggestions,
            showSuggestions: true
          });
        }
        // User pressed the down arrow, increment the index
        else if (e.keyCode === 40) {
          if (activeSuggestion - 1 === suggestions.length) {
            return;
          }
          onChange?.(suggestions[activeSuggestion + 1]);
          setAutoCompleteSuggestions({
            activeSuggestion: activeSuggestion + 1,
            suggestions: suggestions,
            showSuggestions: true
          });
        }
      }
    };
    const inputType = props.type === "password" && showPassword ? "text" : (props.type ?? "text");
    const inputTypeRegexp = props.type ?? "text";
    const isValidText = (text: string) => {
      return /^[a-zA-Z0-9\s/\-?:().,'+àâèéêëîïôöùûüÀÂÈÉÊËÎÏÔÖÙÛÜçÇ]*$/.test(text);
    };
    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string
    ) => {
      if (isChangeEvent(event)) {
        const { value: targetValue } = event.target;
        if (inputTypeRegexp === "text" && !isValidText(targetValue) && !noLatinCheck) {
          setErrorMessage(true);
          setErrorMessageText(t("error.inputRegxp"));
          setTimeout(() => {
            setErrorMessage(false);
            setErrorMessageText(undefined);
          }, 4000);
          return;
        }
        if (props.type === "email") addAutocompleteSuggestions(targetValue ?? "");

        if (onChange) onChange(targetValue ?? "");
      } else {
        onChange?.(event);
      }
    };
    const handleMouseDown = (
      event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement, globalThis.MouseEvent>
    ) => onMouseDown && onMouseDown(event);
    const handleClick = (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onClick && onClick(event);

    function isChangeEvent(
      input: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string
    ): input is React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> {
      return (
        (input as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target !== undefined
      );
    }

    const divAutoCompleteClass = props.type === "email" ? " autocomplete" : "";
    const [missingRequired, setMissingRequired] = useState(false);

    const [errorMessage, setErrorMessage] = useState(false);

    const [errorMessageText, setErrorMessageText] = useState<string | undefined>(error);

    const setRequired = () => setMissingRequired(true);

    const checkRequired = () => {
      if (props.required && !value) setMissingRequired(true);
    };

    useEffect(() => {
      // Listen for window message that check all required fields
      window.addEventListener("checkRequiredFields", checkRequired);
      if (name) {
        window.addEventListener("setRequired-" + name, setRequired);
      }
      return () => {
        window.removeEventListener("checkRequiredFields", checkRequired);
        if (name) {
          window.removeEventListener("setRequired-" + name, setRequired);
        }
      };
    }, []);
    useEffect(() => {
      setErrorMessageText(error);
      setErrorMessage(error ? true : false);
    }, [error]);

    const input = (
      <>
        {inputComponent || (
          <InputWidget
            dataCy={dataCy}
            type={inputType}
            rows={rows}
            value={value ?? ""}
            isPhoneNumber={isPhoneNumber}
            placeholder={placeholder}
            onChange={handleChange}
            onMouseDown={handleMouseDown}
            onKeyDown={onkeydownSuggestion}
            onClick={handleClick}
            formatValue={formatValue}
            onFocus={() => setMissingRequired(false)}
            onBlur={() => {
              if (props.required && !value?.trim()) {
                setMissingRequired(true);
              }
              /*if (props.type === 'email') {
              resetSuggestion()
            }*/
            }}
            required={true}
            maxLength={maxLength}
            ref={ref}
            autoTrim={autoTrim}
            className={twMerge(
              inputClassName,
              (missingRequired || errorMessage || errorMessageText) &&
                "border-red-500 dark:border-red-500"
            )}
            {..._.omit(props, "type")}
          />
        )}
        <SuffixIcon
          dataTooltip={missingRequired ? t("general.required") : undefined}
          className={
            missingRequired || errorMessage ? "border-red-500 text-red-500 dark:text-red-500" : ""
          }
          suffixIcon={
            suffixIcon ? (
              suffixIcon
            ) : missingRequired || errorMessage ? (
              <ExclamationTriangleIcon />
            ) : props.type === "password" ? (
              showPassword ? (
                <EyeSlashIcon />
              ) : (
                <EyeIcon />
              )
            ) : (
              suffixIcon
            )
          }
          onClickSuffixIcon={
            onClickSuffixIcon ? onClickSuffixIcon : () => setShowPassword(!showPassword)
          }
        />
        {!!errorMessageText && (
          <div className="absolute top-13 right-0">
            {" "}
            <Base className="inline font-smebold text-red-500 text-sm">
              {errorMessageText}
            </Base>{" "}
          </div>
        )}
      </>
    );
    return (
      <>
        <div className={twMerge("relative", className, divAutoCompleteClass || "w-full")}>
          {!!label && (
            <label>
              <Text
                type="label"
                className={twMerge("whitespace-nowrap text-left", disabled ? "opacity-40" : "")}
              >
                {label}
                {/*{props.required === false ? ' (' + t('general.optional') + ')' : '*'}*/}
                {props.required === false ? "" : " *"}
              </Text>
              {!!helper && (
                <Text type="info" className={"text-primary"}>
                  {helper}
                </Text>
              )}
            </label>
          )}
          {!!label && <div className={"relative mt-2"}>{input}</div>}
          {!label && input}
          {AutoCompleteSuggestions.showSuggestions && (
            <ul
              className={`absolute top-15 z-50 mt-1 mt-2 max-h-40 w-full list-outside overflow-auto rounded-sm border-2 border-slate-50 bg-white font-semibold text-black duration-300 hover:list-inside hover:border-slate-100 focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 ${
                origin !== FromURL.None
                  ? "focus:border-primary dark:focus:border-primary"
                  : "focus:border-blue-500 dark:focus:border-blue-500"
              } placeholder:font-semibold placeholder:text-slate-400 dark:placeholder:text-slate-600`}
            >
              {AutoCompleteSuggestions.suggestions
                .slice(0, 20)
                .map((suggestion: string, index: number) => {
                  let className = "";
                  // Flag the active suggestion with a class
                  if (index === AutoCompleteSuggestions.activeSuggestion) {
                    className = "bg-slate-50 dark:bg-slate-600 rounded-sm";
                  }
                  return (
                    <li
                      onClick={() => selectSuggestion(suggestion)}
                      key={suggestion}
                      className={twMerge(
                        "my-1 cursor-pointer px-4 py-4 hover:rounded-sm hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-600",
                        className
                      )}
                    >
                      {suggestion}
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </>
    );
  }
);

export type InputWidgetProps = {
  type: HTMLInputElement["type"];
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  formatValue: (str: string) => string;
  value: string;
  rows?: number;
  className?: string;
  isPhoneNumber?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => void;
  onMouseDown: (
    event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement, MouseEvent>
  ) => void | undefined;
  onClick: (
    event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement, MouseEvent>
  ) => void | undefined;
  dataCy?: string;
  autoTrim?: boolean;
};

export const InputWidget = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputWidgetProps>(
  (
    {
      type,
      placeholder,
      required = false,
      minLength,
      maxLength,
      formatValue,
      value,
      rows,
      className,
      isPhoneNumber,
      onChange,
      onMouseDown,
      onClick,
      dataCy,
      autoTrim = true,
      ...props
    },
    ref
  ) => {
    const [onChangeEvent, setOnChangeEvent] = useState<
      React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
    >(undefined);
    const isMobile = useNativeInputs();
    const inputClassName = twMerge(
      maxLength && `w-[${maxLength + 0.5}rem]`,
      "focus:border-primary",
      !maxLength && "w-full",
      (maxLength ?? 0) > 10 && "w-full"
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setOnChangeEvent(event);
      onChange?.(event);
    };

    const handleBlur = () => {
      if (!autoTrim || !onChangeEvent) return;

      const eventTrimed = _.cloneDeep(onChangeEvent);
      eventTrimed.target.value = eventTrimed.target.value.trim();
      handleChange(eventTrimed);
    };

    if (rows) {
      const textareaClass = "w-full";
      return (
        <textarea
          data-cy={dataCy}
          placeholder={placeholder}
          className={twMerge(inputClassName, textareaClass, className)}
          value={formatValue(value || "")}
          rows={rows}
          minLength={minLength}
          maxLength={maxLength}
          required={required}
          onChange={handleChange}
          ref={ref as RefObject<HTMLTextAreaElement>}
          onMouseDown={onMouseDown}
          onClick={onClick}
          {..._.omit(props, "type")}
          onBlur={handleBlur}
        />
      );
    }
    if (type === "date" && !isMobile) {
      return (
        <FormattedInputDate
          placeholder={placeholder}
          className={twMerge(inputClassName, className)}
          value={formatValue(value || "")}
          maxLength={maxLength}
          required={required}
          onChange={onChange}
          onMouseDown={onMouseDown}
          onClick={onClick}
          dataCy={dataCy}
          {..._.omit(props, "type")}
        />
      );
    }
    return (
      <input
        data-cy={dataCy}
        type={type}
        placeholder={placeholder}
        className={twMerge(inputClassName, className)}
        value={formatValue(value || "")}
        maxLength={maxLength}
        required={required}
        inputMode={isPhoneNumber ? "tel" : "text"}
        ref={ref as RefObject<HTMLInputElement>}
        onChange={handleChange}
        onMouseDown={onMouseDown}
        onClick={onClick}
        {..._.omit(props, "type")}
        onBlur={handleBlur}
      />
    );
  }
);

export type SuffixIconProps = {
  suffixIcon?: ReactNode | ReactNode[];
  onClickSuffixIcon?: () => void;
  className?: string;
  dataTooltip?: string;
};

export const SuffixIcon = ({
  suffixIcon,
  onClickSuffixIcon,
  className,
  dataTooltip
}: SuffixIconProps) => {
  const iconStyle = `h-6 w-6  dark:text-white text-primary` + className;

  return (
    <>
      {suffixIcon && (
        <button
          data-tooltip={dataTooltip}
          onClick={(e) => {
            e.preventDefault();
            if (onClickSuffixIcon) onClickSuffixIcon();
          }}
          className="absolute top-1/2 right-4 h-6 w-6 -translate-y-1/2 transform focus:outline-none"
        >
          <div className={iconStyle}>{suffixIcon}</div>
        </button>
      )}
    </>
  );
};

export default InputLabel;
